// Bringing this browser's copy of the dev web app up to date with the Mac's (docs/features/dev-mode.md): the Mac's
// signed list of files — checked against the Mac's pinned identity key before anything is fetched — against the
// one the service worker holds; the text files fetched up front into FILES_CACHE (so a dev page can start before
// any Mac is connected), each checked against its SHA-256 in the list; anything changed or gone dropped. The
// service worker reads the same cache and checks every hash again as it serves; models, sounds and pictures are
// fetched when first asked for, through the page (index.js).
import './gate.js'
import { eagerPaths, manifestDiff } from './switch.js'
import { getFile, getManifest } from './link.js'

const { FILES_CACHE, fileKey, verifyManifest, sha256 } = globalThis.codyDevCode

/// Dev code that failed its check: `path` (or the list itself) and the banner's words.
export class Refused extends Error {
  constructor(path, words) { super(words); this.path = path }
}

/// `link` the dev Mac's MacWindows, `pinned` its identity key as this browser pinned it, `before` the list the
/// worker holds, `base` the app root's path. Resolves with { list, signed: {text, signature}, changed }; throws
/// `Refused` when the list's signature or a file's hash does not check out, or another error.
export async function syncCopy({ link, pinned, before = {}, base, onProgress = () => {} }) {
  const signed = await getManifest(link)
  const list = await verifyManifest(pinned, signed.signature, signed.text)
  if (!list) throw new Refused(null, 'Dev code refused: the file list’s signature didn’t check out')
  const files = list.files
  const cache = await caches.open(FILES_CACHE)
  const key = (path) => fileKey(location.origin, base, path)
  const { changed, removed } = manifestDiff(before, files)
  for (const path of [...removed, ...changed]) await cache.delete(key(path))

  // index.html first: it says whether the copy loads the bundle (dist/) or the source modules
  let index = ''
  if (files['index.html']) {
    const hit = await cache.match(key('index.html'))
    if (hit) index = await hit.text()
    else index = new TextDecoder().decode(await put(cache, link, files, 'index.html', key))
  }
  const missing = []
  for (const path of eagerPaths(files, index)) if (path !== 'index.html' && !(await cache.match(key(path)))) missing.push(path)
  let done = 0
  onProgress(done, missing.length)
  const queue = [...missing]
  const worker = async () => { for (let path; (path = queue.shift());) { await put(cache, link, files, path, key); onProgress(++done, missing.length) } }
  await Promise.all(Array.from({ length: Math.min(6, queue.length) }, worker))
  return { list, signed, changed: changed.length + removed.length > 0 }
}

/// One file into the cache, only when its bytes are the ones the signed list names.
async function put(cache, link, files, path, key) {
  const got = await getFile(link, path)
  if (got.status !== 200) throw new Error(`${path}: ${got.reason || 'status ' + got.status}`)
  if (!files[path] || (await sha256(got.body)) !== files[path].sha256) throw new Refused(path, `Dev code refused: ${path} didn’t match its signed hash`)
  await cache.put(key(path), new Response(got.body.slice(0), { headers: { 'content-type': got.contentType } }))
  return got.body
}
